// Iter84 — Logistics Request Management (Phase 1A).
//
// API helpers + React Query hooks for /api/logistics-requests.
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/logistics-requests`;

const fetchJson = (path) => axios.get(`${API}${path}`).then((r) => r.data);
const postJson = (path, body) => axios.post(`${API}${path}`, body).then((r) => r.data);

export const LR_STAGES = [
  "registered", "assigned", "waiting_for_pricing", "priced",
  "waiting_for_approval", "to_send", "offered",
  "won", "executed", "lost", "cancelled",
  // Phase J.5 (Feb 2026) — Terminal side-outcome.
  "rejected_by_provider",
];
export const LR_TYPES = ["tender", "spot", "sonderfahrt"];

// J.5 — Service-provider rejection reason categories. Must match the
// backend REJECTED_BY_PROVIDER_REASON_CATEGORIES set in
// routes/logistics_requests.py.
export const REJECT_REASON_CATEGORIES = [
  "no_capacity", "rate_too_low", "lane_not_served",
  "equipment_mismatch", "customs_blocked", "blacklisted_loader", "other",
];
export const REJECT_REASON_LABELS_I18N = {
  en: {
    no_capacity: "No capacity",
    rate_too_low: "Rate too low",
    lane_not_served: "Lane not served",
    equipment_mismatch: "Equipment mismatch",
    customs_blocked: "Customs blocked",
    blacklisted_loader: "Blacklisted loader / site",
    other: "Other",
  },
  de: {
    no_capacity: "Keine Kapazität",
    rate_too_low: "Preis zu niedrig",
    lane_not_served: "Strecke nicht bedient",
    equipment_mismatch: "Fahrzeug passt nicht",
    customs_blocked: "Zoll blockiert",
    blacklisted_loader: "Verlader gesperrt",
    other: "Sonstiges",
  },
  hu: {
    no_capacity: "Nincs kapacitás",
    rate_too_low: "Túl alacsony tarifa",
    lane_not_served: "Útvonal nem kiszolgált",
    equipment_mismatch: "Eszközpark nem megfelelő",
    customs_blocked: "Vám blokkolja",
    blacklisted_loader: "Tiltólistás telephely",
    other: "Egyéb",
  },
};

// γ-3-H — Canonical EN/DE/HU stage labels. No parenthetical duality —
// `executed` is just "Executed/Megvalósult/Durchgeführt".
export const STAGE_LABELS_I18N = {
  en: {
    registered: "Registered",
    assigned: "Assigned",
    waiting_for_pricing: "Waiting for pricing",
    priced: "Priced",
    waiting_for_approval: "Waiting for approval",
    to_send: "To send",
    offered: "Offered",
    won: "Won",
    executed: "Executed",
    lost: "Lost",
    cancelled: "Cancelled",
    rejected_by_provider: "Rejected by provider",
  },
  de: {
    registered: "Registriert",
    assigned: "Zugewiesen",
    waiting_for_pricing: "Wartet auf Preis",
    priced: "Bepreist",
    waiting_for_approval: "Wartet auf Freigabe",
    to_send: "Zu senden",
    offered: "Angeboten",
    won: "Gewonnen",
    executed: "Durchgeführt",
    lost: "Verloren",
    cancelled: "Storniert",
    rejected_by_provider: "Abgelehnt vom Dienstleister",
  },
  hu: {
    registered: "Regisztrált",
    assigned: "Hozzárendelve",
    waiting_for_pricing: "Árazásra vár",
    priced: "Árazott",
    waiting_for_approval: "Jóváhagyásra vár",
    to_send: "Kiküldhető",
    offered: "Ajánlat kiküldve",
    won: "Megnyert",
    executed: "Megvalósult",
    lost: "Elveszett",
    cancelled: "Törölt",
    rejected_by_provider: "Elutasítva szolgáltató által",
  },
};

export const STAGE_LABELS = STAGE_LABELS_I18N.en;
export const STAGE_COLORS = {
  registered: "#9ec3ec",
  assigned: "#c89adb",
  waiting_for_pricing: "#f3b14a",
  priced: "#5cd2e6",
  waiting_for_approval: "#b7770d",
  to_send: "#a78bfa",
  offered: "#4ed084",
  won: "#22a35a",
  executed: "#0d6b39",
  lost: "#E24B4A",
  cancelled: "#9198b0",
  rejected_by_provider: "#7e3aa8",
};

export const useLogisticsRequests = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, v); });
  const qs = params.toString();
  return useQuery({
    queryKey: ["logistics-requests", qs],
    queryFn: () => fetchJson(qs ? `?${qs}` : ""),
  });
};

export const useLogisticsRequest = (id) =>
  useQuery({
    queryKey: ["logistics-request", id],
    enabled: !!id,
    queryFn: () => fetchJson(`/${id}`),
  });

export const useLogisticsDashboard = () =>
  useQuery({ queryKey: ["logistics-dashboard"], queryFn: () => fetchJson("/dashboard") });

// γ-3-G — Won deals whose last drop date has passed but aren't marked Executed.
export const useAwaitingExecuted = () =>
  useQuery({ queryKey: ["logistics-awaiting-executed"], queryFn: () => fetchJson("/awaiting-executed/list") });

export const useLogisticsCalendar = (since, until) => {
  const params = new URLSearchParams();
  if (since) params.set("since", since);
  if (until) params.set("until", until);
  return useQuery({
    queryKey: ["logistics-calendar", since, until],
    queryFn: () => fetchJson(`/calendar?${params.toString()}`),
  });
};

export const useCreateLogistics = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => postJson("", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["logistics-requests"] });
      qc.invalidateQueries({ queryKey: ["logistics-dashboard"] });
      qc.invalidateQueries({ queryKey: ["logistics-calendar"] });
    },
  });
};

export const useTransitionLogistics = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => postJson(`/${id}/transition`, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["logistics-requests"] });
      qc.invalidateQueries({ queryKey: ["logistics-dashboard"] });
      qc.invalidateQueries({ queryKey: ["logistics-request", vars.id] });
    },
  });
};

export const usePatchLogistics = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/${id}`, body).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["logistics-requests"] });
      qc.invalidateQueries({ queryKey: ["logistics-request", vars.id] });
    },
  });
};

export const useLogisticsCategories = () =>
  useQuery({
    queryKey: ["logistics-categories"],
    queryFn: () => fetchJson("/categories/list"),
  });

export const useCreateLogisticsCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => postJson("/categories", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logistics-categories"] }),
  });
};

// iter86b — admin marks a category as Key Account (drives C9 dashboard).
export const usePatchLogisticsCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/categories/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logistics-categories"] }),
  });
};

// iter86b — Vehicle types (Autótípus master list).
export const useVehicleTypes = () =>
  useQuery({ queryKey: ["logistics-vehicle-types"], queryFn: () => fetchJson("/vehicle-types/list") });

// iter91 γ-3-A — Surcharge master list (admin-editable, seeded with 15 defaults).
export const useSurcharges = () =>
  useQuery({ queryKey: ["logistics-surcharges"], queryFn: () => fetchJson("/surcharges/list") });

export const SURCHARGE_CATEGORIES = ["pre_informed", "hidden", "added_value"];
export const SURCHARGE_CATEGORY_LABELS = {
  pre_informed: { en: "Pre-informed", de: "Vorab informiert", hu: "Előre informált" },
  hidden:       { en: "Hidden / claim", de: "Versteckt / Anspruch", hu: "Rejtett / utólagos" },
  added_value:  { en: "Added value", de: "Mehrwert", hu: "Hozzáadott érték" },
};
export const SURCHARGE_CATEGORY_TINT = {
  pre_informed: "#185FA5",
  hidden:       "#b22a29",
  added_value:  "#0f8a4a",
};

// iter91 γ-3-A — Loading sides multi-select.
export const LOADING_SIDES = ["rear", "left", "right", "top_crane", "both_sides", "other"];
export const LOADING_SIDE_LABELS = {
  rear:        { en: "Rear",          de: "Heck",                hu: "Hátsó" },
  left:        { en: "Left side",     de: "Linke Seite",         hu: "Bal oldal" },
  right:       { en: "Right side",    de: "Rechte Seite",        hu: "Jobb oldal" },
  top_crane:   { en: "Top (crane)",   de: "Oben (Kran)",         hu: "Felülről (daru)" },
  both_sides:  { en: "Both sides",    de: "Beidseitig",          hu: "Mindkét oldal" },
  other:       { en: "Other",         de: "Sonstige",            hu: "Egyéb" },
};

// iter93 γ-3-C — Tender scheduling + linking + attachments.
export const useTenderSchedule = (horizonMonths = 6) =>
  useQuery({ queryKey: ["tender-schedule", horizonMonths], queryFn: () => fetchJson(`/tenders/schedule?horizon_months=${horizonMonths}`) });

export const useTendersForLinking = (q = "") =>
  useQuery({ queryKey: ["tenders-for-linking", q], queryFn: () => fetchJson(`/tenders/list-for-linking${q ? `?q=${encodeURIComponent(q)}` : ""}`) });

// Upload one attachment to a logistics request. Returns the persisted metadata.
export const uploadAttachment = async (rid, file, kind = "other") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  // Use axios so the global Authorization header (set in src/api/client.js)
  // is sent. We do NOT set Content-Type manually — the browser adds the
  // multipart boundary.
  const res = await axios.post(`${API}/${rid}/attachments`, fd);
  return res.data;
};

export const deleteAttachment = async (rid, aid) => {
  await axios.delete(`${API}/${rid}/attachments/${aid}`);
};

export const attachmentDownloadUrl = (rid, aid) =>
  `${API}/${rid}/attachments/${aid}/download`;


export const useCreateVehicleType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => postJson("/vehicle-types", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logistics-vehicle-types"] }),
  });
};

export const usePatchVehicleType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/vehicle-types/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logistics-vehicle-types"] }),
  });
};

export const useDeleteVehicleType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => axios.delete(`${API}/vehicle-types/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logistics-vehicle-types"] }),
  });
};

// iter86b — Approval rules + queue.
export const useApprovalRules = () =>
  useQuery({ queryKey: ["logistics-approval-rules"], queryFn: () => fetchJson("/approval-rules/current") });

export const usePutApprovalRules = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.put(`${API}/approval-rules/current`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logistics-approval-rules"] }),
  });
};

export const useApprovalState = (rid) =>
  useQuery({
    queryKey: ["logistics-approval-state", rid],
    enabled: !!rid,
    queryFn: () => fetchJson(`/${rid}/approval`),
  });

export const usePendingApprovals = () =>
  useQuery({ queryKey: ["logistics-pending-approvals"], queryFn: () => fetchJson("/pending-approvals/list") });

export const useRequestApproval = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rid) => postJson(`/${rid}/request-approval`, {}),
    onSuccess: (_d, rid) => {
      qc.invalidateQueries({ queryKey: ["logistics-approval-state", rid] });
      qc.invalidateQueries({ queryKey: ["logistics-pending-approvals"] });
    },
  });
};

export const useDecideApproval = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rid, decision, note }) => postJson(`/${rid}/${decision}`, { note: note || null }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["logistics-approval-state", vars.rid] });
      qc.invalidateQueries({ queryKey: ["logistics-pending-approvals"] });
      qc.invalidateQueries({ queryKey: ["logistics-requests"] });
    },
  });
};

// iter87 γ-1 — In-app notification hooks (bell badge scaffold).
export const useInAppUnreadCount = () =>
  useQuery({
    queryKey: ["inapp-notifications-unread"],
    queryFn: () => axios.get(`${BACKEND_URL}/api/notifications/unread-count`).then((r) => r.data),
    refetchInterval: 30000,  // poll every 30s; cheap and avoids WS plumbing for the scaffold
  });

export const useInAppNotifications = () =>
  useQuery({
    queryKey: ["inapp-notifications"],
    queryFn: () => axios.get(`${BACKEND_URL}/api/notifications`).then((r) => r.data),
  });

export const useMarkInAppRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => axios.post(`${BACKEND_URL}/api/notifications/${id}/mark-read`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inapp-notifications"] });
      qc.invalidateQueries({ queryKey: ["inapp-notifications-unread"] });
    },
  });
};

export const useMarkAllInAppRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => axios.post(`${BACKEND_URL}/api/notifications/mark-all-read`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inapp-notifications"] });
      qc.invalidateQueries({ queryKey: ["inapp-notifications-unread"] });
    },
  });
};

// iter86b B7 — Statistics dashboard.
export const useLogisticsStats = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, v); });
  const qs = params.toString();
  return useQuery({
    queryKey: ["logistics-stats", qs],
    queryFn: () => fetchJson(qs ? `/stats/summary?${qs}` : "/stats/summary"),
  });
};

export const exportLogisticsXlsx = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, v); });
  const res = await axios.get(`${API}/export/xlsx?${params.toString()}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sitroom-logistics-requests-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

export const exportLogisticsPdf = async (id, lang, variant) => {
  const params = new URLSearchParams();
  if (lang) params.set("lang", lang);
  if (variant) params.set("variant", variant);
  const qs = params.toString();
  const res = await axios.get(`${API}/${id}/pdf${qs ? `?${qs}` : ""}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  // γ-3-F (iter95d) — Honour the Content-Disposition filename so the INTERNAL
  // marker baked-in by the backend lands on the downloaded file. Fallback to
  // a derived name if the header is missing.
  const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
  const m = /filename=([^;]+)/i.exec(cd);
  const fallback = `${id}_INTERNAL-BELSO_${(variant || "client").toUpperCase()}.pdf`;
  const a = document.createElement("a");
  a.href = url;
  a.download = m ? m[1].trim().replace(/^['"]|['"]$/g, "") : fallback;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};


// ─── I.2 · Surcharge templates (per-customer pre-saved bundles) ─────────────

const TEMPLATES_API = `${BACKEND_URL}/api/surcharge-templates`;

export const useSurchargeTemplates = (customerTenantId) =>
  useQuery({
    queryKey: ["surcharge-templates", customerTenantId || "_all"],
    queryFn: () => axios.get(
      `${TEMPLATES_API}${customerTenantId ? `?customer_tenant_id=${customerTenantId}` : ""}`
    ).then((r) => r.data),
    staleTime: 60_000,
  });

export const useCreateSurchargeTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(TEMPLATES_API, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["surcharge-templates"] }),
  });
};

export const usePatchSurchargeTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tid, patch }) => axios.patch(`${TEMPLATES_API}/${tid}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["surcharge-templates"] }),
  });
};

export const useDeleteSurchargeTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tid) => axios.delete(`${TEMPLATES_API}/${tid}`).then(() => tid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["surcharge-templates"] }),
  });
};
